import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { VacancyComplaintStatus } from '@cookspace-shared-contracts/core';
import { Eye } from 'lucide-react';

import { useListVacancyComplaintsQuery } from '@/api/vacancyComplaintsApi';
import { Select } from '@/components/Select';
import {
    CAFE_ROLE_LABEL,
    COMPLAINT_REASON_LABEL,
    COMPLAINT_STATUS_COLOR,
    COMPLAINT_STATUS_LABEL
} from '@/lib/vacancyComplaintLabels';

const STATUS_OPTIONS: Array<{ value: VacancyComplaintStatus; label: string }> = [
    { value: 'pending', label: 'Новая' },
    { value: 'dismissed', label: 'Закрыта' },
    { value: 'vacancy_hidden', label: 'Вакансия скрыта' }
];

const PAGE_SIZE = 20;

const GRID_COLS =
    'grid-cols-[110px_minmax(0,1.4fr)_160px_minmax(0,1.6fr)_140px_100px]';

function formatDate(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleDateString('ru-RU');
}

export function VacancyComplaintsPage(): ReactElement {
    const navigate = useNavigate();

    const [filterStatus, setFilterStatus] = useState<VacancyComplaintStatus | ''>('pending');
    const [page, setPage] = useState(0);

    const queryArg = useMemo(
        () => ({
            ...(filterStatus ? { status: filterStatus } : {}),
            limit: PAGE_SIZE,
            offset: page * PAGE_SIZE
        }),
        [filterStatus, page]
    );

    const { data, isLoading, isError } = useListVacancyComplaintsQuery(queryArg);

    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const totalPages = total === 0 ? 0 : Math.ceil(total / PAGE_SIZE);

    return (
        <div data-testid="vacancy-complaints-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Жалобы на вакансии</h1>
            </div>

            <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3">
                <div className="w-56">
                    <Select
                        label="Статус"
                        value={filterStatus}
                        onChange={(e) => {
                            setFilterStatus(e.target.value as VacancyComplaintStatus | '');
                            setPage(0);
                        }}
                        placeholder="Все статусы"
                        options={STATUS_OPTIONS}
                        data-testid="vacancy-complaints-filter-status"
                    />
                </div>
            </div>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="vacancy-complaints-error">
                    Не удалось загрузить жалобы
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="vacancy-complaints-table"
                >
                    <div
                        className={`grid ${GRID_COLS} gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600`}
                    >
                        <span>Дата</span>
                        <span>Вакансия</span>
                        <span>Причина</span>
                        <span>Комментарий</span>
                        <span>Статус</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div
                            className="px-3 py-6 text-center text-sm text-gray-500"
                            data-testid="vacancy-complaints-empty"
                        >
                            Жалобы не найдены
                        </div>
                    )}

                    {items.map((item) => (
                        <div
                            key={item.id}
                            className={`grid ${GRID_COLS} items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0`}
                            data-testid={`vacancy-complaint-row-${item.id}`}
                        >
                            <span className="text-xs text-gray-700">
                                {formatDate(item.createdAt)}
                            </span>
                            <span
                                className="truncate text-gray-900"
                                title={`${CAFE_ROLE_LABEL[item.vacancy.role]} · ${item.vacancy.cafeName}`}
                            >
                                {CAFE_ROLE_LABEL[item.vacancy.role]} ·{' '}
                                <span className="text-gray-600">{item.vacancy.cafeName}</span>
                            </span>
                            <span className="text-gray-700">
                                {COMPLAINT_REASON_LABEL[item.reason]}
                            </span>
                            <span
                                className="truncate text-gray-700"
                                title={item.comment ?? undefined}
                            >
                                {item.comment !== null && item.comment !== ''
                                    ? item.comment
                                    : '—'}
                            </span>
                            <span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${COMPLAINT_STATUS_COLOR[item.status]}`}
                                >
                                    {COMPLAINT_STATUS_LABEL[item.status]}
                                </span>
                            </span>
                            <span className="flex justify-end gap-1">
                                <button
                                    type="button"
                                    onClick={() => navigate(`/vacancy-complaints/${item.id}`)}
                                    data-testid={`vacancy-complaint-view-${item.id}`}
                                    className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-100"
                                    aria-label="Открыть"
                                    title="Открыть"
                                >
                                    <Eye size={12} />
                                    Открыть
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {totalPages > 1 && (
                <div
                    className="flex items-center justify-between text-sm text-gray-700"
                    data-testid="vacancy-complaints-pagination"
                >
                    <span>
                        Страница {page + 1} из {totalPages} · всего {total}
                    </span>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                            data-testid="vacancy-complaints-page-prev"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Назад
                        </button>
                        <button
                            type="button"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= totalPages}
                            data-testid="vacancy-complaints-page-next"
                            className="rounded border border-gray-300 px-3 py-1 text-xs transition hover:bg-gray-100 disabled:opacity-50"
                        >
                            Вперёд
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
